import ess from'../../../pip/library/pi-ess/pi-ess.js';

export interface IMemory {
  /**
   * access data from the shared memory block
   *
   * @param value undefined makes a read, null makes a delete, any other value assigns a value
   * @example
   * ```typescript
   * // call permutations
   * memory.shared({}, '*', null);  // delete all
   * memory.shared({}, 'a', null);  // delete key a
   * memory.shared({}, 'a', {});    // write key a
   * memory.shared({}, 'a');        // read key a
   * ```
   */
  shared<T>(fallback: unknown, key: string, value?: T): T;
}

/**
 * make a variable truly safe and undeletable
 */
const makeSafeStore = (object: unknown, key: string, value: unknown) => {
  Object.defineProperty(object, key, {
    configurable: false,
    enumerable: false,
    writable: false,
    value
  });

  return value;
};

const getSafeStore = (key: string, value: () => unknown) => {
  return window[key] === undefined ? makeSafeStore(window, key, value()) : window[key];
};

/**
 * hide all properties and keys in closures for added safety
 */
export default (function () {
  return (function () {
    const vaultKeys = Object.freeze({
      // the shared vault key needs to be deterministically derived per machine,
      // but not something anyone can guess, so nobody accesses it directly.
      //
      shared: window.btoa(`${navigator.userAgent}`)
    });

    /**
     * either retrieve the value or create it, so we only have one shared object
     */
    const vault = Object.freeze({
      shared: getSafeStore(vaultKeys.shared, () => new Map<string, unknown>())
    });

    const notifyEvent = (source: string, action: string, data: unknown) => {
      ess.info({ site: 'memory', source, action, data });
    };

    return (function () {
      class Memory implements IMemory {
        /**
         * access data from the shared memory block
         *
         * @param value undefined makes a read, null makes a delete, any other value assigns a value
         * @example
         * ```typescript
         * // call permutations
         * memory.shared({}, '*', null);  // delete all
         * memory.shared({}, 'a', null);  // delete key a
         * memory.shared({}, 'a', {});    // write key a
         * memory.shared({}, 'a');        // read key a
         * ```
         */
        shared<T>(fallback: unknown, key: string, value?: T): T {
          if (key === '*' && value === null) {
            notifyEvent(key, 'clear-all', value);
            vault.shared.clear();
          } else if (value === null) {
            notifyEvent(key, 'clear', value);
            vault.shared.delete(key);
          } else if (value !== undefined) {
            notifyEvent(key, 'write', value);
            vault.shared.set(key, value);
          } else {
            notifyEvent(key, 'read', value);
          }

          const out = vault.shared.get(key) as T;
          return out === undefined ? (fallback as T) : (out as T);
        }
      }

      return new Memory();
    })();
  })();
})();
